import { useEffect, useState } from "react";
import { Button, Dropdown, Form, Input, Spin, message as antMessage } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { serverFetch } from "@server";
import { requestCompanyRefresh } from "@services/companyRefreshService";

const MESSAGE_DIRECTION_INCOMING = 1;
const MESSAGE_DIRECTION_OUTCOMING = -1;

/** Непрочитано: lastMessageIdIsRead не задан — все сообщения; иначе id > lastMessageIdIsRead */
const isMessageUnread = (msg, dialog) => {
  if (msg?.id == null) return false;
  const readId = dialog?.lastMessageIdIsRead;
  if (readId == null) return true;
  return Number(msg.id) > Number(readId);
};

const Messages = ({
  dialog,
  messages,
  messagesLoading,
  messagesEndRef,
  scrollToBottom,
  setMessages,
  onSent,
  onDialogRead,
  onDialogUnread,
}) => {
  const user = useSelector((s) => s.user);
  const [form] = Form.useForm();
  const [sendLoading, setSendLoading] = useState(false);
  const [markReadLoadingId, setMarkReadLoadingId] = useState(null);
  const [markUnreadLoading, setMarkUnreadLoading] = useState(false);

  useEffect(() => {
    form.resetFields();
  }, [dialog?.id, form]);

  const isIncoming = (direction) => direction === MESSAGE_DIRECTION_INCOMING;

  const sendBlocked = Boolean(dialog?.deleted);
  const sendPlaceholder = dialog?.deleted
    ? "Диалог удалён — отправка недоступна"
    : "Введите сообщение...";

  const handleMarkMessageRead = (msg) => {
    if (!dialog?.id || !isMessageUnread(msg, dialog) || markReadLoadingId != null) return;

    const readId = dialog.lastMessageIdIsRead;
    const msgId = Number(msg.id);
    if (readId != null && Number(readId) >= msgId) return;

    setMarkReadLoadingId(msg.id);
    serverFetch(`Message/Dialog/MarkRead/${dialog.id}?messageId=${msg.id}`, { method: "PUT" })
      .then(() => {
        onDialogRead?.(msg.id);
        onSent?.();
        requestCompanyRefresh();
      })
      .catch((e) => antMessage.error(e.userMessage))
      .finally(() => setMarkReadLoadingId(null));
  };

  const getLastIncomingMessageId = () => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.direction === MESSAGE_DIRECTION_INCOMING && messages[i]?.id != null) {
        return messages[i].id;
      }
    }
    return null;
  };

  const handleMarkUnread = () => {
    if (!dialog?.id || markUnreadLoading) return;

    setMarkUnreadLoading(true);
    serverFetch(`Message/Dialog/MarkUnread/${dialog.id}`, { method: "PUT" })
      .then(() => {
        onDialogUnread?.(getLastIncomingMessageId());
        onSent?.();
        requestCompanyRefresh();
      })
      .catch((e) => antMessage.error(e.userMessage))
      .finally(() => setMarkUnreadLoading(false));
  };

  const handleCopyMessage = (msg) => {
    const text = (msg?.body ?? "").toString();
    if (!text.trim()) {
      antMessage.warning("Нечего копировать");
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => antMessage.success("Сообщение скопировано"),
      () => antMessage.error("Не удалось скопировать")
    );
  };

  const onSend = () => {
    if (!dialog?.id || !user?.companyId || sendBlocked) return;
    const body = form.getFieldValue("body")?.trim();
    if (!body) return;

    setSendLoading(true);
    serverFetch("Message/Message", {
      method: "POST",
      bodyData: {
        companyId: dialog.companyId,
        dialogId: dialog.id,
        body,
        direction: MESSAGE_DIRECTION_OUTCOMING,
        status: 1,
        userId: user?.id ?? null,
      },
    })
      .then((created) => {
        setMessages((prev) => {
          if (prev.some((m) => m.id === created.id)) return prev;
          return [...prev, created];
        });
        form.setFieldsValue({ body: "" });
        setTimeout(scrollToBottom, 50);
        onDialogRead?.(created.id, true);
        onSent?.();
        requestCompanyRefresh();
      })
      .catch((e) => antMessage.error(e.userMessage))
      .finally(() => setSendLoading(false));
  };

  return (
    <div className="dialog-messages-tab">
      <Spin spinning={messagesLoading}>
        <div className="dialog-messages-scroll">
          {messages.map((msg) => {
            const metaParts = [];
            if (msg.direction === MESSAGE_DIRECTION_INCOMING) metaParts.push("Входящее");
            else if (msg.direction === MESSAGE_DIRECTION_OUTCOMING) metaParts.push("Исходящее");
            if (msg.createdOn) metaParts.push(dayjs(msg.createdOn).format("YYYY-MM-DD HH:mm:ss"));
            if (msg.id != null) metaParts.push(`#${msg.id}`);
            const metaLine = metaParts.join(" • ");
            const unread = isMessageUnread(msg, dialog);
            const messageMenu = {
              items: [
                {
                  key: "copy",
                  label: "Копировать",
                  onClick: ({ domEvent }) => {
                    domEvent?.stopPropagation();
                    handleCopyMessage(msg);
                  },
                },
                {
                  key: "mark-unread",
                  label: "Пометить как непрочитанное",
                  disabled: markUnreadLoading,
                  onClick: ({ domEvent }) => {
                    domEvent?.stopPropagation();
                    handleMarkUnread();
                  },
                },
              ],
            };
            return (
            <Dropdown key={msg.id} menu={messageMenu} trigger={["contextMenu"]}>
            <div
              className={`dialogs-message-bubble ${isIncoming(msg.direction) ? "incoming" : "outcoming"}${unread ? " dialogs-message-bubble-unread" : ""}`}
              onClick={unread ? () => handleMarkMessageRead(msg) : undefined}
              role={unread ? "button" : undefined}
              tabIndex={unread ? 0 : undefined}
              onKeyDown={
                unread
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleMarkMessageRead(msg);
                      }
                    }
                  : undefined
              }
            >
              {unread ? <span className="dialogs-message-unread-dot" aria-hidden /> : null}
              {metaLine ? (
              <div className="dialogs-message-meta dialog-msg-inline-meta">
                {metaLine}
              </div>
              ) : null}
              <div className="dialogs-message-body">{msg.body}</div>
              {(msg.userName || msg.UserName) && (
                <div className="dialogs-message-id">{msg.userName || msg.UserName}</div>
              )}
            </div>
            </Dropdown>
          );})}
          <div ref={messagesEndRef} />
        </div>
      </Spin>

      <div className="dialogs-send-row dialog-messages-send">
        <Form form={form} onFinish={onSend} layout="vertical" className="dialog-messages-send-form">
          <Form.Item name="body" style={{ marginBottom: 0 }}>
            <Input.TextArea
              placeholder={sendPlaceholder}
              autoSize={{ minRows: 2, maxRows: 6 }}
              maxLength={1024}
              disabled={sendBlocked}
              onPressEnter={(e) => {
                if (sendBlocked) return;
                if (!e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={sendLoading} disabled={sendBlocked}>
              Отправить
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Messages;
